"use client";

import {
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { ChevronDown } from "lucide-react";
import { classNames } from "../ui/class-names";

const minFontSize = 8;
const maxFontSize = 24;
const fontSizeStep = 0.5;

export type FontSizeInputProps = {
  value: number;
  onChange: (fontSize: number) => void;
  /** Accessible name; defaults to "Font size: N points". */
  "aria-label"?: string;
  /** Visible label before the number; defaults to "Size". Pass null to hide. */
  label?: string | null;
  /** White bordered field chrome when nested in FontAndSizeControl. */
  field?: boolean;
  id?: string;
};

export function clampFontSize(value: number) {
  return Math.min(Math.max(value, minFontSize), maxFontSize);
}

export function FontSizeInput({
  value,
  onChange,
  "aria-label": ariaLabel,
  label = "Size",
  field = false,
  id,
}: Readonly<FontSizeInputProps>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [rawValue, setRawValue] = useState(String(value));

  // Keep the controlled input value in sync with external prop changes, but
  // only when the field is not focused so that the user's typing state is
  // not overwritten while they are editing the value.
  useLayoutEffect(() => {
    const input = inputRef.current;

    if (!input || document.activeElement === input) {
      return;
    }

    setRawValue(String(value));
  }, [value]);

  function commit(raw: string) {
    const parsed = Number.parseFloat(raw);

    if (Number.isNaN(parsed)) {
      setRawValue(String(value));
      return;
    }

    const clamped = clampFontSize(parsed);

    setRawValue(String(clamped));

    if (clamped !== value) {
      onChange(clamped);
    }
  }

  function applyValue(nextValue: number) {
    const clamped = clampFontSize(nextValue);
    const clampedText = String(clamped);

    setRawValue(clampedText);

    if (clamped !== value) {
      onChange(clamped);
    }
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setRawValue(event.target.value);
  }

  function handleWheel(event: React.WheelEvent<HTMLInputElement>) {
    event.preventDefault();

    const direction = event.deltaY > 0 ? -1 : 1;
    applyValue(value + direction * fontSizeStep);
  }

  function handleBlur() {
    commit(rawValue);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      commit(rawValue);
      event.currentTarget.blur();
    }
  }

  function focusInput() {
    inputRef.current?.focus();
  }

  return (
    <div
      className={classNames(
        "inline-flex items-center text-xs text-zinc-950",
        field
          ? "w-full cursor-text rounded-md border border-zinc-200 bg-white transition hover:border-zinc-300 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand"
          : "shrink-0 rounded-lg border border-zinc-200 bg-zinc-50 p-0.5 transition hover:bg-zinc-100 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand",
      )}
      onClick={field ? focusInput : undefined}
    >
      <div
        className={classNames(
          "inline-flex w-full items-center gap-1",
          field ? "justify-between px-2.5 py-2" : "gap-1.5 px-2.5 py-1",
        )}
      >
        {label ? <span className="text-zinc-500">{label}</span> : null}
        <span className="inline-flex min-w-0 flex-1 items-center gap-1">
          <input
            ref={inputRef}
            id={id}
            type="number"
            min={minFontSize}
            max={maxFontSize}
            step={fontSizeStep}
            value={rawValue}
            onChange={handleChange}
            onWheel={handleWheel}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel ?? `Font size: ${value} points`}
            className={classNames(
              "min-w-0 bg-transparent font-semibold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
              field ? "w-full text-left" : "w-10 text-right",
            )}
          />
          <span className="shrink-0 text-zinc-500">pt</span>
        </span>
        {field ? (
          <ChevronDown
            aria-hidden="true"
            className="size-4 shrink-0 text-zinc-500"
          />
        ) : null}
      </div>
    </div>
  );
}
